<script lang="ts" setup>
import CheckCircleFillIcon from "@/components/icon/CheckCircleFillIcon.vue";
import PlusIcon from "@/components/icon/PlusIcon.vue";
import MateMediaPreview from "@/components/mobile/overlay/MateMediaPreview.vue";
import ScrollLoadingList, {
  ScrollLoadingListInstance,
} from "@/components/mobile/ScrollLoadingList.vue";
import api from "@/modules/api";
import { isAudio, isImage, isVideo } from "@/modules/common";
import { useMate } from "@/stores/mate";
import { MateSelectorParam, useUI } from "@/stores/ui";
import { Mate, MateMedia, MediaWithMateMediaId } from "@/types/entity";
import { useName } from "@/use/common";
import { Image } from "vant";
import { computed, Ref, ref, watch } from "vue";
import BaseImg from "../common/BaseImg.vue";
import { useI18n } from "vue-i18n";
// 1. 常量
// 2. Props & Emit & Expose
export interface MateGalleryInstance {
  refresh: (clearFilter?: boolean) => Promise<void>;
  selectedMediaList: Ref<(MediaWithMateMediaId & { selected?: boolean })[]>;
}

const props = withDefaults(
  defineProps<{
    verticalExpand?: boolean;
    mateId?: number;
    filter?: 1 | 2 | 3; // 1-图片；2-视频；3-音频
    selectable?: boolean;
    enableUpload?: boolean;
    maxSelectCount?: number;
    scroller?: Element | null;
    enableMateSwitch?: boolean;
  }>(),
  {
    selectable: false,
    enableUpload: false,
    maxSelectCount: 1,
    enableMateSwitch: false,
    verticalExpand: true,
  }
);

const emit = defineEmits<{
  (e: "update:mateId", mateId?: number): void;
}>();
// 3. 内部状态
const mateStore = useMate();
const uiStore = useUI();
const currentFilter = ref<1 | 2 | 3 | null>(props.filter ?? null); // null: 全部, 1: 图片, 2: 视频, 3: 音频
const allMediaList = ref<MateMedia[]>([]); // 所有媒体列表
// 实际渲染的媒体列表（根据过滤器）
const mediaList = computed<MediaWithMateMediaId[]>(() => {
  return allMediaList.value.map((item) => ({
    mateMediaId: item.id,
    ...item,
    ...item.media,
  }));
});
const { t } = useI18n();
// 预览遮罩层状态
const isShowPreview = ref(false);
const startPosition = ref(0);
// 选中状态
const selectedMediaIdSet = ref<Set<number>>(new Set());
const selectedMediaList = computed<MediaWithMateMediaId[]>(() => {
  return mediaList.value.filter((item) =>
    selectedMediaIdSet.value.has(item.id!)
  );
});
const scrollListRef = ref<ScrollLoadingListInstance | null>(null);
const currentMate = computed<Mate | undefined>(() => {
  return props.mateId
    ? mateStore.getMyMateById(props.mateId)
    : mateStore.userMate;
});

const uploadTargetMateId = ref<number | undefined>(undefined);
// 4. Lifecycle
const scrollLoad = async (reload = false) => {
  let cursor;
  if (!reload && allMediaList.value.length > 0) {
    cursor = btoa(
      JSON.stringify({
        id: allMediaList.value[allMediaList.value.length - 1].id,
        _pointsToNextItems: true,
      })
    );
  }
  const { page } = await api.readMateMediaPage({
    filter: {
      mateId: props.mateId,
      type: currentFilter.value === null ? undefined : currentFilter.value,
    },
    pagination: { page: 1, perPage: 15 },
    sort: { field: "id", order: "desc" },
    cursor,
  });
  if (page.data.length === 0) {
    return false;
  }
  allMediaList.value.push(...page.data);
  return true;
};

const onSwitchMateFilter = () => {
  const param: MateSelectorParam = {
    selectedMateId: currentMate.value?.id,
    mateOnly: false,
    selectionMode: "selection",
    title: t("Message.filterAlbum"),
    switchMate: false,
    callback: (mate: Mate) => {
      emit(
        "update:mateId",
        mate.id === mateStore.userMate.id ? undefined : mate.id
      );
      uiStore.showMateSelector = false;
    },
  };
  uiStore.popMateSelector(param);
};

const onUploadClick = () => {
  uploadTargetMateId.value = currentMate.value?.id;
  const param: MateSelectorParam = {
    selectedMateId: -1,
    mateOnly: false,
    selectionMode: "selection",
    title: t("Message.uploadToWhichAlbum"),
    switchMate: false,
    callback: (mate: Mate) => {
      uploadTargetMateId.value = mate.id;
      uiStore.showMateSelector = false;
      isBottomShow.value = true;
    },
  };
  uiStore.popMateSelector(param);
};

watch(currentFilter, () => {
  allMediaList.value = [];
  scrollListRef.value?.resetScrollLoadingState();
});

watch(currentMate, () => {
  refresh();
});

// 5. Methods
/** 从服务端重新获取gallery列表 */
async function refresh(clearFilter = false) {
  allMediaList.value = [];
  if (clearFilter) {
    currentFilter.value = null;
  }
  scrollListRef.value?.resetScrollLoadingState();
}

const onSelect = (idx: number) => {
  if (props.selectable) {
    const target = mediaList.value[idx];
    if (
      !selectedMediaIdSet.value.has(target.id!) &&
      selectedMediaList.value.length >= props.maxSelectCount
    )
      return;
    if (selectedMediaIdSet.value.has(target.id!)) {
      selectedMediaIdSet.value.delete(target.id!);
    } else {
      selectedMediaIdSet.value.add(target.id!);
    }
  } else {
    isShowPreview.value = true;
    startPosition.value = idx;
  }
};
const afterMateMediaDelete = (mediaId: number) => {
  const index = allMediaList.value.findIndex(
    (item) => item.media.id === mediaId
  );
  allMediaList.value.splice(index, 1);
};

const isMyMate = computed(() => {
  console.log(currentMate.value?.id);

  return mateStore.isMyMateById(currentMate.value?.id);
});

const onSimpleUploadClick = () => {
  uploadTargetMateId.value = currentMate.value?.id;
  isBottomShow.value = true;
};

defineExpose<MateGalleryInstance>({
  refresh, // method: 刷新gallery列表
  selectedMediaList, // computed: 已选中的媒体列表
});

const isBottomShow = ref(false);
</script>
<template>
  <div
    class="gap-2 h-full"
    :class="{
      'flex flex-col gap-2 h-full': !verticalExpand,
      'grid grid-rows-[auto_1fr] overflow-hidden': verticalExpand,
    }"
  >
    <div class="flex flex-col gap-2">
      <div v-if="enableMateSwitch" class="px-2 flex justify-between">
        <button
          class="btn btn-xs btn-outline rounded"
          @click="onSwitchMateFilter"
        >
          <span>{{
            currentMate?.isUser
              ? $t("Buttons.allAlbum")
              : `${useName(currentMate?.name)}`
          }}</span>
          <i class="iconify mingcute--down-small-line" />
        </button>
        <div
          v-if="enableUpload && isMyMate"
          class="btn btn-outline btn-xs rounded"
          @click="onUploadClick"
        >
          {{ $t("Buttons.upload") }}
        </div>
      </div>
      <div class="flex justify-between items-center px-2">
        <div class="tabs tabs-boxed w-fit rounded border">
          <a
            :class="{ 'tab-active': currentFilter === null }"
            class="tab tab-xs"
            @click="currentFilter = null"
          >
            {{ $t("User.gallery.all") }}
          </a>
          <a
            :class="{ 'tab-active': currentFilter === 1 }"
            class="tab tab-xs"
            @click="currentFilter = 1"
          >
            <i class="iconify mingcute--pic-line" />
          </a>
          <a
            :class="{ 'tab-active': currentFilter === 2 }"
            class="tab tab-xs"
            @click="currentFilter = 2"
          >
            <i class="iconify mingcute--video-camera-line" />
          </a>
          <a
            :class="{ 'tab-active': currentFilter === 3 }"
            class="tab tab-xs"
            @click="currentFilter = 3"
          >
            <i class="iconify mingcute--music-line"></i>
          </a>
        </div>
        <button
          v-if="enableUpload && !enableMateSwitch && isMyMate"
          class="btn btn-primary-content btn-xs rounded"
          @click="onSimpleUploadClick"
        >
          <plus-icon class="icon-base" />
          {{ $t("Buttons.add") }}
        </button>
      </div>
    </div>
    <scroll-loading-list
      ref="scrollListRef"
      :on-scroll-load="scrollLoad"
      :scroller="scroller"
      :class="{
        'overflow-y-auto h-full': verticalExpand,
        'min-h-full': !verticalExpand,
      }"
    >
      <div class="grid grid-cols-[repeat(auto-fill,minmax(100px,1fr))] gap-0.5">
        <div
          v-for="(item, idx) in mediaList"
          :key="item.id"
          class="w-full aspect-[1/1] overflow-hidden"
          @click="onSelect(idx)"
        >
          <div
            v-if="isImage(item.url)"
            class="bg-base-200 w-full h-full relative flex items-center justify-center"
          >
            <BaseImg :media="item" :width="200" class="h-full" />
            <div
              v-if="selectedMediaIdSet.has(item.id!)"
              class="backdrop-filter backdrop-blur-sm absolute inset-0 bg-neutral-900 opacity-50"
            ></div>
            <check-circle-fill-icon
              v-if="selectedMediaIdSet.has(item.id!)"
              class="icon-base right-1 bottom-1 absolute text-info"
            />
          </div>
          <div
            v-else-if="isVideo(item.url)"
            class="w-full h-full bg-base-200 relative"
          >
            <Image
              :src="item.videoCoverUrl!"
              class="w-full h-full"
              fit="cover"
              crossorigin="anonymous"
            />
            <div
              class="absolute right-1 bottom-1 bg-neutral p-1 rounded flex items-center justify-center opacity-80"
            >
              <i
                class="iconify mingcute--video-camera-line text-neutral-content"
              />
            </div>
            <div
              v-if="selectedMediaIdSet.has(item.id!)"
              class="backdrop-filter backdrop-blur-sm absolute inset-0 bg-neutral-900 opacity-50"
            ></div>
            <check-circle-fill-icon
              v-if="selectedMediaIdSet.has(item.id!)"
              class="icon-base right-1 bottom-1 absolute text-info"
            />
          </div>
          <div
            v-else-if="isAudio(item.url)"
            class="w-full h-full bg-base-200 relative"
          >
            <div class="flex justify-center items-center h-full relative">
              <i class="iconify mingcute--play-circle-fill"></i>

              <div
                class="absolute right-1 bottom-1 bg-neutral p-1 rounded flex items-center justify-center opacity-50"
              >
                <i
                  class="iconify mingcute--music-line text-neutral-content"
                ></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </scroll-loading-list>
    <mate-media-preview
      v-model:show="isShowPreview"
      :enable-delete="isMyMate"
      :mate-media-list="mediaList"
      :start-position="startPosition"
      @after-delete="afterMateMediaDelete"
    />

    <media-upload-popup
      v-model:show="isBottomShow"
      :mate-id="uploadTargetMateId!"
      @finished="refresh"
    />
  </div>
</template>
<style>
.media-preview .van-image-preview__cover {
  top: unset;
  bottom: 0;
  left: 0;
  right: 0;
}
</style>
